import dayjs from 'dayjs';

import { getBanner } from '@/api/lounge-board/bulletin/banner';
import { getCompanySummary } from '@/api/pc-api/v1/{authInfoId}/company/summary';
import { getContractById } from '@/api/pc-api/v1/{authInfoId}/contract/{id}';
import {
  ContractListForDealerParams,
  getContractListForDealer
} from '@/api/pc-api/v1/{authInfoId}/contract/list/search-for-dealer';
import {
  ContractListForPartnerParams,
  getContractListForPartner
} from '@/api/pc-api/v1/{authInfoId}/contract/list/search-for-partner';
import { getDyingContract } from '@/api/pc-api/v1/{authInfoId}/dashboard/contract/dying';
import { getRejectReason } from '@/api/pc-api/v1/{authInfoId}/dashboard/review/reject-reason';
import { getStatusCounts } from '@/api/pc-api/v1/{authInfoId}/dashboard/svc/status-counts';
import { getPartner } from '@/api/pc-api/v1/{authInfoId}/partner/{partnerId}/init-info';
import { getSettleUnitType } from '@/api/pc-api/v1/{authInfoId}/partner/{partnerId}/settle-unit-type';
import { getPartnerDetailsInfo } from '@/api/pc-api/v1/{authInfoId}/partner/details/info';
import { getPartnerDetailsSvcList } from '@/api/pc-api/v1/{authInfoId}/partner/details/svc-list';
import { getUserList } from '@/api/pc-api/v1/{authInfoId}/partner/user/list';
import { getCompleteRanking } from '@/api/pc-api/v1/{authInfoId}/service-stat/complete-ranking';
import { getRequestRangeStatics } from '@/api/pc-api/v1/{authInfoId}/service-stat/request-range-statics';
import { getRequestRangeSummary } from '@/api/pc-api/v1/{authInfoId}/service-stat/request-range-summary';
import { getRequestRanking, RequestRankingOrder } from '@/api/pc-api/v1/{authInfoId}/service-stat/request-ranking';
import { getRequestRankingSummary } from '@/api/pc-api/v1/{authInfoId}/service-stat/request-ranking-summary';
import { getSettleManagerInfo } from '@/api/pc-api/v1/{authInfoId}/settle/base-info';
import { getSettleList, Settle } from '@/api/pc-api/v1/{authInfoId}/settle/list';
import { getSettleListForPartner } from '@/api/pc-api/v1/{authInfoId}/settle/list-for-partner';
import { getSettleBaseInfo } from '@/api/pc-api/v1/{authInfoId}/settle-base-info';
import { getSettleBaseDetailInfo } from '@/api/pc-api/v1/{authInfoId}/settle-base-info/settle-base-detail-info';
import { getSettleInvoiceDetail } from '@/api/pc-api/v1/{authInfoId}/settle-invoice/details/{settleInvoiceId}';
import { getSettleInvoiceList } from '@/api/pc-api/v1/{authInfoId}/settle-invoice/list';
import { getChargedOnly, getStatSearch, UsageSearchQuery } from '@/api/pc-api/v1/{authInfoId}/stat/search';
import { getBusinessPerformance } from '@/api/pc-api/v1/{authInfoId}/statistics/business-performance';
import { getFirstOpen } from '@/api/pc-api/v1/{authInfoId}/statistics/organizations/first-open';
import { getNewlyContractCount } from '@/api/pc-api/v1/{authInfoId}/statistics/organizations/newly-contract/count';
import { getRequestedCount } from '@/api/pc-api/v1/{authInfoId}/statistics/organizations/requested/count';
import { getSvcDetails } from '@/api/pc-api/v1/{authInfoId}/svc/details';
import { getSvcListCompact } from '@/api/pc-api/v1/{authInfoId}/svc/list/compact';
import { getSvcListName, SvcName } from '@/api/pc-api/v1/{authInfoId}/svc/list/name';
import { getSvcListSearch, SvcListSearchParams } from '@/api/pc-api/v1/{authInfoId}/svc/list/search';
import { getTaxInvoiceIssuanceList } from '@/api/pc-api/v1/{authInfoId}/tax-invoice-issuance/list';
import { getInvitedUserList } from '@/api/pc-api/v1/{authInfoId}/user/list/invited';
import { getInvitingUserList } from '@/api/pc-api/v1/{authInfoId}/user/list/inviting';
import { getProductList } from '@/api/pc-api/v1/product/list';
import { getPrice } from '@/api/pc-api/v2/{authInfoId}/price';
import { BizTargetType } from '@/constants/bizTargetType';
import { BizType } from '@/constants/bizType';
import { ContractSearchPeriodType, ContractSearchStatusType, ContractStatus } from '@/constants/contract';
import { SETTLE_PREV_MONTH_FORMATTED_STR, SETTLE_START_MONTH_FORMATTED_STR } from '@/constants/date';
import { DatePickerType } from '@/constants/datePicker';
import { SETTLE_ID, SVC_ID } from '@/constants/routes';
import { SettleUnitType } from '@/constants/settle';
import { PreLoaderFunction } from '@/lib/react-router-dom/createPrivateRoutes';
import { InitialContractParams } from '@/pages/:AUTH_INFO_ID/contract/add/constants/searchParams';
import { getDealerContractPageData } from '@/pages/:AUTH_INFO_ID/contract/dealer/utils/api';
import { ReportPeriod } from '@/pages/:AUTH_INFO_ID/stats/report/types/ReportPeriod';
import { YESTERDAY_DATE_FORMATTED } from '@/pages/:AUTH_INFO_ID/stats/usage/hooks/useUsageSearchParams';
import { partnerStore } from '@/store/partner';
import { assertNonEmptyArray, findValueOrReturnFirst, resolveObject } from '@/types/utils';
import { getDefaultMonthlyDates, getDefaultWeeklyDates, getDefaultYearlyDates, getStatType } from '@/utils/report';

const preLoadMembers: PreLoaderFunction = async ({ authData }) => {
  const id = authData.partnerId || authData.dealerId || '';
  const [invitingUserList, invitedUserList] = await Promise.all([getInvitingUserList(id), getInvitedUserList(1, id)]);

  return {
    invitingUserList: invitingUserList.data.data,
    invitedUserList: invitedUserList.data
  };
};

const preLoadBusiness: PreLoaderFunction = async ({ authData }) => {
  const { partnerId, dealerId } = authData;
  const {
    data: { data }
  } = await getCompanySummary(partnerId || dealerId || '');

  return data;
};

const preLoadUsage: PreLoaderFunction = async ({ authData }) => {
  const { dealerId, partnerId, bizType } = authData;
  const params: UsageSearchQuery = {
    id: dealerId || partnerId || '',
    datePickerType: DatePickerType.DAILY,
    start: YESTERDAY_DATE_FORMATTED,
    end: YESTERDAY_DATE_FORMATTED,
    chargedOnly: getChargedOnly(bizType),
    page: 1
  };

  const { data } = await getStatSearch(params);
  return data;
};

const preLoadAddSvc: PreLoaderFunction = async ({ params }) => {
  const { partnerId = '' } = params;
  const { data } = await getPartnerDetailsInfo(partnerId);

  return {
    partnerDetailsInfo: data.data
  };
};

const preLoadPartnerDetail: PreLoaderFunction = async ({ params }) => {
  const { partnerId = '' } = params;
  const [userList, partnerDetailsInfo, partnerDetailsSvcList] = await Promise.all([
    getUserList({ partnerId, page: 1 }),
    getPartnerDetailsInfo(partnerId),
    getPartnerDetailsSvcList({ partnerId, page: 1 })
  ]);

  return {
    userList: userList.data,
    partnerDetailsInfo: partnerDetailsInfo.data.data,
    partnerDetailsSvcList: partnerDetailsSvcList.data
  };
};

const preLoadSvcDetail: PreLoaderFunction = async ({ params }) => {
  const { svcId = '' } = params;
  const { data } = await getSvcDetails(svcId);
  return data.data;
};

const preLoadEditSvc: PreLoaderFunction = async ({ params }) => {
  const { svcId = '' } = params;
  const { data } = await getSvcDetails(svcId);
  return data.data;
};

const preLoadSvcList: PreLoaderFunction = async ({ authData }) => {
  const { data } = await getPartnerDetailsSvcList({ partnerId: authData.partnerId || '', page: 1 });
  return data;
};

const preLoadPartnerContractForPartner: PreLoaderFunction = async ({ authData }) => {
  const params: ContractListForPartnerParams = {
    id: authData.partnerId || '',
    page: 1,
    searchContractDateType: ContractSearchPeriodType.ALL,
    startedAt: '',
    finishedAt: '',
    name: '',
    productIds: [],
    searchContractStatusType: ContractSearchStatusType.ALL,
    searchContractStatus: undefined
  };
  const {
    data: { data: productList }
  } = await getProductList(authData.bizType);
  const { data: contractList } = await getContractListForPartner(params);

  const isDirectPartner = authData.bizTargetType === BizTargetType.DIRECT_PARTNER;
  if (!isDirectPartner) return { productList, contractList };

  const {
    data: { data: price }
  } = await getPrice(authData.partnerId || '', authData.bizType);
  return { productList, contractList, price };
};

const preLoadDealerContract: PreLoaderFunction = async ({ authData }) => {
  const { data } = await getDealerContractPageData(authData.dealerId || '');
  return data;
};

const preLoadPartnerContractForDealer: PreLoaderFunction = async ({ authData, search }) => {
  const type = search.get('type') as ContractSearchStatusType | null;
  const status = search.get('status') as ContractStatus | null;

  const params: ContractListForDealerParams = {
    id: authData.dealerId || '',
    page: 1,
    searchContractDateType: search.get('startedAt') ? 'START' : '',
    startedAt: search.get('startedAt') || '',
    finishedAt: search.get('finishedAt') || '',
    name: '',
    productIds: [],
    searchContractStatusType: type || ContractSearchStatusType.ALL,
    searchContractStatus: status || undefined
  };

  const [productList, contractListForDealer] = await Promise.all([
    getProductList(authData.bizType),
    getContractListForDealer(params)
  ]);

  return {
    productList: productList.data.data,
    contractListForDealer: contractListForDealer.data
  };
};

const preLoadPartnerContractDetail: PreLoaderFunction = async ({ authData, params, search }) => {
  const { partnerId = '' } = params;
  const [svcListName, productList] = await Promise.all([
    getSvcListName(partnerId).then(({ data }) => data.data),
    getProductList(authData.bizType).then(({ data }) => data.data)
  ]);

  const isDealer = authData.bizTargetType === BizTargetType.DEALER;
  const unitType = isDealer ? SettleUnitType.DEALER : (await getSettleUnitType(partnerId)).data.data;

  // svcListName.svc_names는 비어있을 수 없다.
  assertNonEmptyArray(svcListName.svcNames);

  const initialSvcId = findValueOrReturnFirst(svcListName.svcNames, search.get(SVC_ID), 'id');
  const initialSettleList = (await getSettleList(initialSvcId)).data.data;

  const settleIdInParam = search.get(SETTLE_ID);
  const initialSettle = initialSettleList.find((settle) => settle.id === settleIdInParam) ?? initialSettleList.at(0);

  const isProdSettle = initialSettle && !initialSettle.test;
  const isUnitTypeSettle = unitType === SettleUnitType.SETTLE;
  const initialSettleManagerInfo =
    isProdSettle && isUnitTypeSettle ? (await getSettleManagerInfo(initialSettle.id)).data.data : undefined;

  return {
    svcListName,
    productList,
    unitType,
    initialSettleList,
    initialSvcId,
    initialSettle,
    initialSettleManagerInfo
  };
};

const preLoadAddContract: PreLoaderFunction = async ({ authData, search }) => {
  const partnerId = search.get(InitialContractParams.partnerId) || authData.partnerId;
  const svcId = search.get(InitialContractParams.svcId);

  let initialSvcList: SvcName[] = [];
  let initialSettleList: Settle[] = [];

  if (partnerId) {
    // 이용기관 id가 지정되었다면 기관 목록을 불러옴
    const { data: serviceNameData } = await getSvcListName(partnerId);
    initialSvcList = serviceNameData.data.svcNames;

    if (svcId) {
      // 기관 id도 지정되어있다면 세틀 목록도 불러옴
      const { data: settleData } = await getSettleList(svcId);
      initialSettleList = settleData.data;
    }
  }

  // 현재 bizType에 맞는 상품 목록을 불러옴
  const { bizType } = authData;
  const {
    data: { data: productList }
  } = await getProductList(bizType);

  // 가능하다면 SettleUnitType을 불러옴
  const { bizTargetType } = authData;
  const isDirectPartner = bizTargetType === BizTargetType.DIRECT_PARTNER;

  let initialUnitType: SettleUnitType | undefined;

  const targetPartnerId = isDirectPartner
    ? authData.partnerId // 직계약이면 스스로의 id를 사용
    : partnerId; // 딜러사면 search param에 있는 id를 사용

  if (targetPartnerId) {
    const resp = await getSettleUnitType(targetPartnerId);
    initialUnitType = resp.data.data;
  }

  return { initialSvcList, initialSettleList, initialUnitType, productList };
};

const preLoadEditContract: PreLoaderFunction = async ({ params }) => {
  const { contractId = '' } = params;
  const { data } = await getContractById(contractId);
  return data.data;
};

const preLoadPartner: PreLoaderFunction = async ({ authData }) => {
  const { data } = await getPartner(authData.partnerId || '');
  return data.data;
};

const preLoadPartnerList: PreLoaderFunction = async () => {
  const { page, searchedAppId, searchedPlatformId, searchedBusinessNumber, searchedCompanyName, searchedSvcName } =
    partnerStore.getState();

  const params: SvcListSearchParams = {
    page,
    appId: searchedAppId,
    platformId: searchedPlatformId,
    businessNumber: searchedBusinessNumber,
    companyName: searchedCompanyName,
    svcName: searchedSvcName
  };
  const { data } = await getSvcListSearch(params);
  return data;
};

const preLoadSettleInvoice: PreLoaderFunction = async () => {
  const start = SETTLE_START_MONTH_FORMATTED_STR;
  const end = SETTLE_PREV_MONTH_FORMATTED_STR;
  const { data } = await getSettleInvoiceList({ start, end });
  return data.data;
};

const preLoadSettleInvoiceDetail: PreLoaderFunction = async ({ params }) => {
  const { settleInvoiceId = '' } = params;
  const { data } = await getSettleInvoiceDetail({ settleInvoiceId });
  return data.data;
};

const preLoadSettleTaxInvoice: PreLoaderFunction = async () => {
  const start = SETTLE_START_MONTH_FORMATTED_STR;
  const end = SETTLE_PREV_MONTH_FORMATTED_STR;
  const { data } = await getTaxInvoiceIssuanceList({ start, end });
  return data.data;
};

const preLoadSettleBaseInfo: PreLoaderFunction = async () => {
  const {
    data: { data: settleBaseInfo }
  } = await getSettleBaseInfo();

  const isDealerOrPartner = settleBaseInfo.unitType !== SettleUnitType.SETTLE;
  if (isDealerOrPartner) return settleBaseInfo;

  const {
    data: { data: settleList }
  } = await getSettleListForPartner();
  const managementSettle = settleList.filter((settle) => !settle.isTest);
  const {
    data: { data: settleBaseDetailInfo }
  } = await getSettleBaseDetailInfo(managementSettle[0].id);
  return { ...settleBaseInfo, settleList: managementSettle, settleBaseDetailInfo };
};

const preLoadDashboardInfo: PreLoaderFunction = async ({ authData }) => {
  const { targetType, bizType, dealerId, partnerId } = authData;
  const isDealer = targetType === BizTargetType.DEALER;

  const isMydata = bizType === BizType.MYDATA_CORP || bizType === BizType.MYDATA_PROVIDER;
  const isEproof = bizType === BizType.EPROOF;
  /** 마이데이터, 모바일전자증명은 대시보드에 통계 요약 지표를 노출하지 않음 */
  const businessPerformancePromise =
    isMydata || isEproof
      ? undefined
      : getBusinessPerformance({
          bizType,
          targetId: (isDealer ? dealerId : partnerId) || null,
          calendarType: DatePickerType.WEEKLY,
          startDate: dayjs().startOf('week').subtract(1, 'week').add(1, 'day').format('YYYY-MM-DD'),
          endDate: dayjs().startOf('week').format('YYYY-MM-DD')
        });

  if (!isDealer) {
    const [banner, businessPerformance] = await Promise.all([getBanner(), businessPerformancePromise]);

    return {
      banner: banner?.data.data,
      businessPerformance: businessPerformance?.data.data
    };
  }

  const [banner, statusCounts, dyingContract, rejectReason, businessPerformance] = await Promise.all([
    getBanner(),
    getStatusCounts(),
    getDyingContract(),
    getRejectReason(),
    businessPerformancePromise
  ]);

  return {
    banner: banner?.data.data,
    statusCounts: statusCounts.data.data,
    dyingContract: dyingContract.data.data,
    rejectReason: rejectReason.data.data,
    businessPerformance: businessPerformance?.data.data
  };
};

const preLoadStatsReport: PreLoaderFunction = async ({ authData, search }) => {
  const { bizType, bizTargetType, dealerId, partnerId } = authData;
  const isDealer = bizTargetType === BizTargetType.DEALER;
  const isEdoc = bizType === BizType.EDOC;
  const targetId = isDealer ? dealerId : partnerId;

  const tab = (search.get('tab') || ReportPeriod.WEEK) as ReportPeriod;

  const getDefaultDates = () => {
    if (tab === ReportPeriod.WEEK) return getDefaultWeeklyDates();
    if (tab === ReportPeriod.YEAR) return getDefaultYearlyDates();
    return getDefaultMonthlyDates();
  };

  const getCalendarType = (): DatePickerType.WEEKLY | DatePickerType.MONTHLY | DatePickerType.YEARLY => {
    if (tab === ReportPeriod.WEEK) return DatePickerType.WEEKLY;
    if (tab === ReportPeriod.YEAR) return DatePickerType.YEARLY;
    return DatePickerType.MONTHLY;
  };

  const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDates();
  const startDate = search.get('startDate') || defaultStartDate;
  const endDate = search.get('endDate') || defaultEndDate;
  const calendarType = getCalendarType();
  const statType = getStatType(tab);

  const commonParams = {
    bizType,
    targetId: targetId || '',
    calendarType,
    startDate,
    endDate
  };

  const promises = {
    svcList: getSvcListCompact().then((res) => res.data.data),
    businessPerformance: getBusinessPerformance(commonParams).then((res) => res.data.data),
    requestRangeSummary: getRequestRangeSummary({ svcId: undefined, statDate: startDate, statType }).then(
      (res) => res.data.data
    ),
    requestRangeStatics: getRequestRangeStatics({ svcId: undefined, statDate: startDate, statType }).then(
      (res) => res.data.data
    ),

    // Dealer만 필요한 데이터
    firstOpen: isDealer ? getFirstOpen(commonParams).then((res) => res?.data.data) : undefined,
    newlyContractCount: isDealer ? getNewlyContractCount(commonParams).then((res) => res?.data.data) : undefined,
    requestedCount: isDealer ? getRequestedCount(commonParams).then((res) => res?.data.data) : undefined,
    requestRankingSummary: isDealer
      ? getRequestRankingSummary({ statType, statDate: startDate }).then((res) => res.data.data)
      : undefined,
    requestRanking: isDealer
      ? getRequestRanking({ rankingOrder: RequestRankingOrder.DESC, statType, statDate: startDate, size: 7 }).then(
          (res) => res.data.data
        )
      : undefined,

    // EDOC이고 Dealer인 경우 추가 데이터
    completeRanking:
      isEdoc && isDealer
        ? getCompleteRanking({ statType, statDate: startDate, size: 10 }).then((res) => res.data.data)
        : undefined
  };

  const data = await resolveObject(promises);
  return data;
};

export {
  preLoadMembers,
  preLoadBusiness,
  preLoadUsage,
  preLoadAddSvc,
  preLoadPartnerDetail,
  preLoadSvcDetail,
  preLoadEditSvc,
  preLoadSvcList,
  getSvcListCompact,
  preLoadPartnerContractForPartner,
  preLoadDealerContract,
  preLoadPartnerContractForDealer,
  preLoadPartnerContractDetail,
  preLoadAddContract,
  preLoadEditContract,
  preLoadPartner,
  preLoadPartnerList,
  preLoadSettleInvoice,
  preLoadSettleInvoiceDetail,
  preLoadSettleTaxInvoice,
  preLoadSettleBaseInfo,
  preLoadDashboardInfo,
  preLoadStatsReport
};
